import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, TrendingUp, TrendingDown, Star, Award, Flame, ArrowRight, BarChart } from 'lucide-react';
import { Market, MarketOption } from "../types.ts";
import { MarketEngine, MarketDataFromDb } from "../lib/marketEngine.ts";
import { XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { supabase } from "../lib/db.ts";
import { useAuth } from "../lib/auth.tsx";
import { logger } from "../services/logger.ts";
import { chartColors } from "../config/brand.ts";
import { useResolutionCountdown } from '../hooks/useResolutionCountdown.ts';

const marketEngine = new MarketEngine();

interface FeaturedMarketCardProps {
  market: Market;
  onWatchlistChange?: (marketId: string, isWatchlisted: boolean) => void;
}

export function FeaturedMarketCard({ market, onWatchlistChange }: FeaturedMarketCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [graphData, setGraphData] = useState<Array<{ timestamp: string; [key: string]: string | number }>>([]);
  const [loading, setLoading] = useState(true);
  const [isWatchlisted, setIsWatchlisted] = useState(market.is_watchlisted || false);
  const [isUpdating, setIsUpdating] = useState(false);

  const resolutionCountdown = useResolutionCountdown(market.resolution_time, {
    enabled: market.status === 'active',
  });

  useEffect(() => {
    let cancelled = false;
    
    const fetchGraphData = async () => {
      try {
        const data = await marketEngine.getMarketOptionHistory(market.id, 'all');
        if (!cancelled) {
          setGraphData(data);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching graph data:', error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchGraphData();
    
    return () => {
      cancelled = true;
    };
  }, [market.id]);

  const marketOptions = React.useMemo(() => 
    Array.isArray(market.market_options) ? market.market_options : [], 
    [market.market_options]
  );

  const winningOption = React.useMemo(() => 
    market.status === 'resolved' && market.winning_option_id
      ? marketOptions.find(option => option.id === market.winning_option_id)
      : null,
    [market.status, market.winning_option_id, marketOptions]
  );

  const sortedOptions = React.useMemo(() => 
    marketOptions.length > 0 
      ? [...marketOptions].sort((a, b) => (b.probability ?? 0) - (a.probability ?? 0))
      : [],
    [marketOptions]
  );

  const winningOptionPricing = React.useMemo(() => {
    if (!winningOption) return null;
    
    try {
      const pricingMarketData: MarketDataFromDb = {
        id: market.id,
        title: market.title,
        is_private: market.is_private,
        category: market.category,
        description: market.description,
        created_by: market.created_by,
        status: market.status,
        total_bets: market.total_bets,
        market_options: market.market_options,
        market_pools: market.marketPools,
        is_dynamic: market.is_dynamic,
        engine_type: market.engine_type,
        lmsr_b: market.lmsr_b,
      };
      return marketEngine.calculatePricing(pricingMarketData, winningOption.id);
    } catch (error) {
      logger.error(`Error calculating pricing for winning option ${winningOption.id}:`, error);
      return null;
    }
  }, [winningOption, market]);

  const imageUrl = React.useMemo(() => market.image_url || '', [market.image_url]);

  const priceChange = typeof market.price_change_24h === 'number' ? market.price_change_24h : 0;
  const isPositiveChange = priceChange >= 0;

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  useEffect(() => {
    if (market.is_watchlisted !== undefined) {
      setIsWatchlisted(market.is_watchlisted);
    }
  }, [market.is_watchlisted]);

  const handleToggleWatchlist = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      navigate('/login', { state: { from: `/markets/${market.id}` } });
      return;
    }
    
    if (isUpdating) return;
    
    setIsUpdating(true);
    
    try {
      const { error } = await supabase.rpc('toggle_watchlist', {
        p_market_id: market.id
      });
      
      if (!error) {
        const newWatchlistState = !isWatchlisted;
        setIsWatchlisted(newWatchlistState);
        onWatchlistChange?.(market.id, newWatchlistState);
      } else {
        logger.error('Error toggling watchlist:', error);
      }
    } catch (error) {
      logger.error('Exception when toggling watchlist:', error);
    } finally {
      setIsUpdating(false);
    }
  }, [user, navigate, isUpdating, isWatchlisted, market.id, onWatchlistChange]);

  const renderFeaturedOption = (option: MarketOption, index: number) => (
    <div
      key={option.id || index}
      className="flex items-center justify-between py-1.5 md:py-2 px-2.5 rounded-lg transition-all duration-200 hover:opacity-80"
      style={{ backgroundColor: 'rgba(92, 179, 173, 0.15)' }}
    >
      <span className="text-sm md:text-xs text-gray-300 truncate pr-2 font-medium">{option.name}</span>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-sm md:text-xs font-bold text-white">{(option.probability ?? 0).toFixed(0)}%</span>
        <span
          className="text-[10px] md:text-[9px] font-semibold px-1.5 py-0.5 rounded"
          style={{ backgroundColor: 'rgba(92, 179, 173, 0.25)', color: '#5cb3ad' }}
        >
          {option.odds && option.odds > 1000 ? '∞' : `${option.odds?.toFixed(2)}x`}
        </span>
      </div>
    </div>
  );

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-gray-900/95 backdrop-blur-sm px-3 py-2 rounded-lg shadow-xl border border-gray-700">
        <p className="text-[10px] text-gray-400 mb-1.5 font-medium">{formatDate(label)}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center justify-between gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }}></div>
              <span className="text-gray-300 truncate max-w-[100px]">{entry.name}</span>
            </div>
            <span className="font-bold text-white">{entry.value.toFixed(1)}%</span>
          </div>
        ))}
      </div>
    );
  };

  // Resolved market card UI
  if (market.status === 'resolved' && winningOption) {
    return (
      <div 
        className="relative min-h-0 h-auto md:h-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl overflow-hidden cursor-pointer group"
        style={{ 
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden'
        }}
        onClick={() => navigate(`/markets/${market.id}`)}
      >
        <p className="pointer-events-none absolute bottom-3 right-4 z-10 text-[9px] text-gray-400 text-right leading-tight max-w-[45%]">
          Market volume{' '}
          <span className="font-semibold text-white">
            R{market.totalPool?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || 0}
          </span>
        </p>
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative flex h-auto md:h-full">
          {/* Left Content Section */}
          <div className="flex-1 p-6 flex flex-col">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/30">
                  <Award className="w-3.5 h-3.5" />
                  Resolved
                </span>
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-white/10 text-gray-300">
                  {market.category}
                </span>
              </div>
              <button
                type="button"
                onClick={handleToggleWatchlist}
                disabled={isUpdating}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200 group/star"
              >
                <Star className={`w-4 h-4 transition-all ${isWatchlisted ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400 group-hover/star:text-yellow-400'}`} />
              </button>
            </div>

            {/* Market Title */}
            <div className="flex items-start gap-4 mb-5">
              {imageUrl && (
                <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 ring-2 ring-white/10">
                  <img src={imageUrl} alt={market.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
              )}
              <h2 className="text-xl font-bold text-white leading-tight line-clamp-2 group-hover:text-blue-300 transition-colors">
                {market.title}
              </h2>
            </div>

            {/* Winner Section */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="bg-gradient-to-r from-emerald-500/20 to-blue-500/20 rounded-xl p-5 border border-emerald-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1.5 bg-emerald-500/30 rounded-lg">
                    <Award className="w-5 h-5 text-emerald-400" />
                  </div>
                  <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Winning Outcome</span>
                </div>
                <p className="text-2xl font-bold text-white mb-3">{winningOption.name}</p>
                {winningOptionPricing && (
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-400">Final:</span>
                      <span className="font-bold text-white">{winningOptionPricing.yesPercentage.toFixed(1)}%</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-400">Return:</span>
                      <span className="font-bold text-emerald-400">
                        {winningOptionPricing.odds === Number.MAX_SAFE_INTEGER ? '—' : `${winningOptionPricing.odds.toFixed(2)}x`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Stats Footer */}
            <div className="flex items-center gap-4 mt-5 pt-4 border-t border-white/10">
              <div className="flex items-center gap-1.5 text-gray-400">
                <BarChart className="w-4 h-4" />
                <span className="text-sm font-medium text-white">{market.total_bets || 0}</span>
                <span className="text-xs">trades</span>
              </div>
            </div>
          </div>

          {/* Right Graph Section - Hidden on mobile */}
          <div className="hidden md:flex w-[380px] flex-col p-6 border-l border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Price History</h3>
            </div>
            
            <div className="flex-1 -mx-2 min-h-[200px]">
              {loading ? (
                <div className="w-full h-full min-h-[200px] flex items-center justify-center opacity-50">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                    <span className="text-xs text-gray-500">Loading chart...</span>
                  </div>
                </div>
              ) : graphData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220} minWidth={200}>
                  <AreaChart data={graphData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      {market.market_options?.map((option: MarketOption, index: number) => (
                        <linearGradient key={`gradient-resolved-${option.id}`} id={`gradient-resolved-${option.id}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={chartColors[index % chartColors.length]} stopOpacity={0.3} />
                          <stop offset="100%" stopColor={chartColors[index % chartColors.length]} stopOpacity={0} />
                        </linearGradient>
                      ))}
                    </defs>
                    <XAxis
                      dataKey="timestamp"
                      tickFormatter={formatDate}
                      tick={{ fill: '#6B7280', fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tickFormatter={(value) => `${value}%`}
                      domain={[0, 100]}
                      ticks={[0, 50, 100]}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6B7280', fontSize: 10 }}
                      width={40}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {market.market_options?.map((option: MarketOption, index: number) => (
                      <Area
                        key={option.id}
                        type="monotone"
                        dataKey={option.name}
                        stroke={chartColors[index % chartColors.length]}
                        strokeWidth={2}
                        fill={`url(#gradient-resolved-${option.id})`}
                        name={option.name}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="w-full h-full min-h-[200px] flex items-center justify-center">
                  <div className="text-center">
                    <TrendingUp className="w-10 h-10 mx-auto mb-2 text-gray-600" />
                    <p className="text-sm text-gray-500">No chart data</p>
                  </div>
                </div>
              )}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-white/10">
              {sortedOptions.slice(0, 3).map((option, index) => (
                <div key={option.id} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: chartColors[index % chartColors.length] }}></div>
                  <span className="text-xs text-gray-400 truncate max-w-[80px]">{option.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Active market card UI
  return (
    <div 
      className="relative min-h-0 h-full bg-gradient-to-br from-slate-900 via-slate-850 to-slate-900 rounded-2xl overflow-hidden cursor-pointer group shadow-2xl"
      style={{ 
        willChange: 'transform',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden'
      }}
      onClick={() => navigate(`/markets/${market.id}`)}
    >
      <p className="pointer-events-none hidden md:block absolute bottom-3 right-4 z-10 text-[9px] text-gray-400 text-right leading-tight max-w-[45%]">
        Market volume{' '}
        <span className="font-semibold text-white">
          R{market.totalPool?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || 0}
        </span>
      </p>
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-wayja-teal/10 to-wayja-light-teal/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/4"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-wayja-slate/10 to-wayja-teal/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4"></div>
      <div className="absolute top-1/2 left-1/2 w-[300px] h-[300px] bg-wayja-dark-teal/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      
      <div className="relative flex h-full min-h-0">
        {/* Left Content Section - Compact */}
        <div className="w-full md:w-[320px] lg:w-[360px] flex-shrink-0 p-4 md:p-5 flex flex-col h-full min-h-0 min-w-0 border-r border-white/5">
          {/* Header with badges */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-gradient-to-r from-wayja-teal to-wayja-teal text-white shadow-lg shadow-wayja-teal/25">
                <Flame className="w-3 h-3" />
                Featured
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse"></span>
                Active
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/10 text-gray-300">
                {market.category}
              </span>
            </div>
            <button
              type="button"
              onClick={handleToggleWatchlist}
              disabled={isUpdating}
              className="p-1.5 rounded-lg bg-white/5 hover:bg-white/15 border border-white/10 hover:border-yellow-500/50 transition-all duration-300 group/star flex-shrink-0"
            >
              <Star className={`w-3.5 h-3.5 transition-all duration-300 ${isWatchlisted ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400 group-hover/star:text-yellow-400'}`} />
            </button>
          </div>

          {/* Market Title with Image */}
          <div className="flex items-start gap-2.5 mb-3">
            {imageUrl && (
              <div className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0 ring-1 ring-white/10 group-hover:ring-wayja-teal/50 transition-all duration-300">
                <img src={imageUrl} alt={market.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="text-sm md:text-base font-bold text-white leading-tight line-clamp-2 group-hover:text-wayja-light-teal transition-colors duration-300">
                {market.title}
              </h2>
              <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-gray-500 uppercase tracking-wide">Market resolution</span>
                  <div className="flex items-center gap-1 text-gray-300">
                    <Clock className="w-3 h-3 flex-shrink-0" />
                    <span className="font-medium">{resolutionCountdown}</span>
                  </div>
                </div>
                {priceChange !== 0 && (
                  <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] ${isPositiveChange ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                    {isPositiveChange ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                    <span className="font-semibold">{isPositiveChange ? '+' : ''}{priceChange.toFixed(1)}%</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Options — top 2 by probability only (no scroll); full outcomes on market page */}
          {sortedOptions.length > 0 && (
            <div className="flex-1 min-h-0 flex flex-col mb-1 md:mb-2">
              <div className="flex items-center justify-between mb-1 md:mb-1.5">
                <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-widest">Options</span>
              </div>
              <div className="md:hidden pr-1 space-y-1">
                {sortedOptions.slice(0, 2).map(renderFeaturedOption)}
              </div>
              <div className="hidden md:flex md:flex-1 flex-col pr-1 space-y-1.5 min-h-0">
                {sortedOptions.slice(0, 2).map(renderFeaturedOption)}
              </div>
            </div>
          )}

          {/* Stats Footer — mobile: volume + trades left; Trade CTA md+ only */}
          <div className="mt-auto pt-2 border-t border-white/10 flex flex-wrap items-center justify-start gap-x-4 gap-y-1 md:flex-nowrap md:justify-between">
            <div className="flex flex-wrap items-center justify-start gap-x-4 gap-y-0.5 text-gray-400">
              <div className="text-[9px] leading-tight">
                <span className="text-gray-500">Market volume </span>
                <span className="font-semibold text-white">
                  R{market.totalPool?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || 0}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <BarChart className="w-3 h-3 flex-shrink-0" />
                <span className="text-xs font-semibold text-white">{market.total_bets || 0}</span>
                <span className="text-[9px] text-gray-500">trades</span>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-1 text-wayja-teal group-hover:text-wayja-light-teal text-xs font-semibold">
              <span>Trade</span>
              <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </div>
        </div>

        {/* Right Graph Section - Takes most of the space, Hidden on mobile */}
        <div className="hidden md:flex flex-1 flex-col p-4 bg-gradient-to-l from-black/10 to-transparent">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Price History</h3>
            {/* Chart Legend - inline */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {sortedOptions.slice(0, 3).map((option, index) => (
                <div key={option.id} className="flex items-center gap-1.5">
                  <div 
                    className="w-2 h-2 rounded-full" 
                    style={{ backgroundColor: chartColors[index % chartColors.length] }}
                  ></div>
                  <span className="text-[10px] text-gray-400 font-medium truncate max-w-[70px]">{option.name}</span>
                </div>
              ))}
              {sortedOptions.length > 3 && (
                <span className="text-[10px] text-gray-500">+{sortedOptions.length - 3} more</span>
              )}
            </div>
          </div>
          
          {/* Chart container - Full height */}
          <div className="flex-1 min-h-0">
            {loading ? (
              <div className="w-full h-full flex items-center justify-center opacity-50">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-10 h-10 border-2 border-wayja-teal/30 border-t-wayja-teal rounded-full animate-spin"></div>
                  <span className="text-xs text-gray-500 font-medium">Loading chart...</span>
                </div>
              </div>
            ) : graphData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={graphData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <defs>
                    {market.market_options?.map((option: MarketOption, index: number) => (
                      <linearGradient key={`gradient-active-${option.id}`} id={`gradient-active-${option.id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={chartColors[index % chartColors.length]} stopOpacity={0.4} />
                        <stop offset="50%" stopColor={chartColors[index % chartColors.length]} stopOpacity={0.15} />
                        <stop offset="100%" stopColor={chartColors[index % chartColors.length]} stopOpacity={0} />
                      </linearGradient>
                    ))}
                  </defs>
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={formatDate}
                    tick={{ fill: '#9CA3AF', fontSize: 11, fontWeight: 500 }}
                    axisLine={{ stroke: '#374151', strokeWidth: 1 }}
                    tickLine={false}
                    interval="preserveStartEnd"
                    dy={8}
                  />
                  <YAxis
                    tickFormatter={(value) => `${value}%`}
                    domain={[0, 100]}
                    ticks={[0, 25, 50, 75, 100]}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#9CA3AF', fontSize: 11, fontWeight: 500 }}
                    width={40}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  {market.market_options?.map((option: MarketOption, index: number) => (
                    <Area
                      key={option.id}
                      type="monotone"
                      dataKey={option.name}
                      stroke={chartColors[index % chartColors.length]}
                      strokeWidth={2.5}
                      fill={`url(#gradient-active-${option.id})`}
                      name={option.name}
                      dot={false}
                      activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-white/5 flex items-center justify-center">
                    <TrendingUp className="w-8 h-8 text-gray-600" />
                  </div>
                  <p className="text-sm text-gray-500 font-medium">No chart data available</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
